import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { useNotifications } from "@/contexts/NotificationContext";
import { Loader2, Mail, Check, X, Users } from "lucide-react";

/**
 * /invites — all of the user's pending group invites.
 * Each row reads "<Sender Name> invited you to '<Group Name>'" — never
 * generic Born Day phrasing, per Steve's brand rule.
 */
export default function Invites() {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState({});
  const [err, setErr] = useState("");
  const { refresh: refreshNotifs } = useNotifications();
  const navigate = useNavigate();

  const load = async () => {
    setErr("");
    try {
      const { data } = await api.get("/invites");
      setRows(data || []);
    } catch (e) {
      setErr(formatError(e));
    }
  };

  useEffect(() => { load(); }, []);

  const act = async (inviteId, action) => {
    setBusy((b) => ({ ...b, [inviteId]: action }));
    setErr("");
    try {
      const { data } = await api.post(`/invites/${inviteId}/${action}`);
      refreshNotifs && refreshNotifs();
      if (action === "accept" && data?.group_id) {
        navigate("/groups");
        return;
      }
      await load();
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setBusy((b) => ({ ...b, [inviteId]: null }));
    }
  };

  if (rows === null) {
    return (
      <AppShell>
        <div className="grid place-items-center py-16"><Loader2 className="animate-spin" /></div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="flex flex-col gap-5 max-w-2xl">
        <div>
          <div className="text-xs uppercase tracking-widest" style={{ color: "var(--text-dim)" }}>
            Inbox
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold" style={{ letterSpacing: "-0.04em" }}>
            Pending invites
          </h1>
          <p style={{ color: "var(--text-dim)" }} className="mt-1">
            People who have invited you to join their groups. Accepting adds you as a member with full access.
          </p>
        </div>

        {err && (
          <div className="text-sm px-3 py-2 rounded"
               style={{ background: "color-mix(in srgb, #ff3b30 15%, transparent)", color: "#ff3b30" }}
               data-testid="invites-error">
            {err}
          </div>
        )}

        {rows.length === 0 && (
          <div className="bd-surface p-8 text-center" data-testid="invites-empty">
            <Mail size={28} style={{ color: "var(--text-dim)" }} className="mx-auto mb-2" />
            <p className="text-sm" style={{ color: "var(--text-dim)" }}>
              No pending invites. When someone invites you, it&apos;ll appear here and in the alert bar at the top.
            </p>
          </div>
        )}

        <ul className="space-y-2">
          {rows.map((inv) => (
            <li
              key={inv.invite_id}
              className="bd-surface p-4 flex items-start gap-3"
              data-testid={`invite-${inv.invite_id}`}
            >
              <div
                className="w-10 h-10 grid place-items-center rounded shrink-0"
                style={{ background: "var(--primary)", color: "var(--primary-fg)" }}
              >
                <Users size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold">
                  <strong>{inv.sender_name}</strong> invited you to{" "}
                  <strong>&ldquo;{inv.group_name}&rdquo;</strong>
                </div>
                <div className="text-xs mt-0.5" style={{ color: "var(--text-dim)" }}>
                  Sent {inv.created_at?.slice(0, 10)}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  className="bd-btn"
                  onClick={() => act(inv.invite_id, "accept")}
                  disabled={busy[inv.invite_id] === "accept"}
                  data-testid={`invite-accept-${inv.invite_id}`}
                >
                  {busy[inv.invite_id] === "accept"
                    ? <Loader2 size={12} className="animate-spin" />
                    : <><Check size={12} /> Accept</>}
                </button>
                <button
                  className="bd-btn bd-btn-ghost"
                  onClick={() => act(inv.invite_id, "decline")}
                  disabled={busy[inv.invite_id] === "decline"}
                  style={{ color: "var(--text-dim)" }}
                  data-testid={`invite-decline-${inv.invite_id}`}
                >
                  <X size={12} /> Decline
                </button>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </AppShell>
  );
}
